/**
 * Usage Example:
 *
 * ```javascript
 * // Get the scrollable element and control elements by their IDs
 * const element = document.getElementById('scrollable-element');
 * const leftBtn = document.getElementById('left-button');
 * const rightBtn = document.getElementById('right-button');
 *
 * // Create a new Scrollable instance
 * const scrollable = new Scrollable({
 *   scrollableRef: element,
 *   leftController: leftBtn,
 *   rightController: rightBtn
 * });
 *
 * // Initialize the Scrollable instance with the DOM element
 * scrollable.initialize();
 * ```
 */

export class Scrollable {
  constructor ({
    scrollableRef,
    leftController,
    rightController
  }) {
    this.scrollThreshold = 5;
    this.scrollableRef = scrollableRef;
    this.leftController = leftController;
    this.rightController = rightController;
    this.isScrollableX = false;
    this.isScrollableY = false;
    this.scrollX = 0;
    this.scrollY = 0;
    this.controllersWidth = 50;
    this.isAtStart = true;
    this.isAtEnd = false;
  }

  initialize () {
    this.updateScrollableFlags();
    this.attachEventListeners();
  }

  destroy () {
    this.detachEventListeners();
  }

  attachEventListeners () {
    this.scrollableRef.addEventListener('scroll', this.handleNodeScroll.bind(this));

    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollableFlags();
    });

    this.resizeObserver.observe(this.scrollableRef);

    if (this.leftController) {
      this.leftController.addEventListener('click', this.scrollToLeft.bind(this));
    }
    if (this.rightController) {
      this.rightController.addEventListener('click', this.scrollToRight.bind(this));
    }
  }

  detachEventListeners () {
    this.scrollableRef.removeEventListener('scroll', this.handleNodeScroll.bind(this));

    if (this.leftController) {
      this.leftController.removeEventListener('click', this.scrollToLeft.bind(this));
    }
    if (this.rightController) {
      this.rightController.removeEventListener('click', this.scrollToRight.bind(this));
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  handleNodeScroll (event) {
    this.scrollX = event.target.scrollLeft;
    this.scrollY = event.target.scrollTop;
    this.isAtStart = this.scrollX <= this.scrollThreshold;
    this.isAtEnd = this.scrollX >= (this.scrollableRef.scrollWidth - this.scrollableRef.clientWidth - this.scrollThreshold);

    this.leftController.classList.toggle('hidden', this.isAtStart);
    this.rightController.classList.toggle('hidden', this.isAtEnd);
  }

  updateScrollableFlags () {
    const node = this.scrollableRef;
    this.isScrollableX = node.scrollWidth > node.clientWidth;
    this.isScrollableY = node.scrollHeight > node.clientHeight;

    this.leftController.classList.toggle('hidden', true);
    this.rightController.classList.toggle('hidden', !this.isScrollableX);
  }

  scrollNodeTo (x, y) {
    this.scrollableRef.scrollTo({
      top: y ?? 0,
      left: x ?? 0,
      behavior: 'smooth'
    });
  }

  scrollToLeft () {
    const scrollSize = this.scrollableRef.clientWidth - this.controllersWidth;
    const position = this.scrollX - scrollSize;
    this.scrollNodeTo(Math.max(0, position), null);
  }

  scrollToRight () {
    const scrollSize = this.scrollableRef.clientWidth - this.controllersWidth;
    const maxScrollX = this.scrollableRef.scrollWidth - this.scrollableRef.clientWidth + this.controllersWidth;
    const position = this.scrollX + scrollSize;
    this.scrollNodeTo(Math.min(maxScrollX, position), null);
  }

  scrollToElement (targetElement) {
    if (!this.isScrollableX && !this.isScrollableY) return;
    if (!this.scrollableRef || !targetElement) return;

    const containerRect = this.scrollableRef.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const isInViewportX = targetRect.right <= containerRect.right && targetRect.left >= containerRect.left;
    if (isInViewportX) return;

    const scrollLeft = this.scrollX + (targetRect.left - containerRect.left) - this.controllersWidth;

    this.scrollNodeTo(scrollLeft, 0);
  }
}
